import { Tooltip } from '@mui/material';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

import { demographicsCalculation, NILL } from '@lib/constants';
import moment from '@lib/moment';

export const getEventStatus = (event) => {
  if (event.status === 'COMPLETED' || event.status === 'CANCELED') {
    return event.status;
  }
  return 'INCOMPLETE';
}

export const getEventStatusIcon = (event) => getStatusIcon(getEventStatus(event));

export const getStatusOrd = (event) => {
  switch (getEventStatus(event)) {
    case 'COMPLETED':
      return 2;
    case 'CANCELED':
      return 0;
    case 'INCOMPLETE':
    default:
      return 1;
  }
};

export const getEventDates = (event) => {
  if (!event.sessionSummary?.first || !event.sessionSummary?.last) {
    return NILL;
  }
  const start = moment(event.sessionSummary.first).format('M/D/yyyy');
  const end = moment(event.sessionSummary.last).format('M/D/yyyy');
  if (start === end) {
    return start;
  } else {
    return `${start} - ${end}`;
  }
}

export const getCurriculumName = (event) => {
  if (event.event_type === 'BOOTH') {
    return 'Booth';
  }
  if (!event?.curriculum) {
    return '';
  }
  if (event.curriculum.custom) {
    return event.curriculum_name;
  }
  return event.curriculum.name;
}

/**
 * Convert a lesson into an Autocomplete dropdown option object.
 *
 * @param lesson
 * @returns {{id, label: *, audience}|{id: null, label: string, audience: string, custom: boolean}}
 */
export const toLessonOption = (lesson) => {
  if (!lesson) {
    return {
      id: null,
      label: '',
      audience: '',
      custom: false,
    };
  }
  return {
    id: lesson.id,
    label: lesson.curriculum_name || lesson.booth_topic || lesson.lesson_name,
    audience: lesson.audience,
    custom: lesson.custom,
  };
};

export const getStatusIcon = (status) => {
  switch (status) {
    case 'COMPLETED':
      return (
        <Tooltip title="Completed">
          <CheckCircleIcon color="success" />
        </Tooltip>
      );
    case 'CANCELED':
      return (
        <Tooltip title="Canceled">
          <CancelRoundedIcon color="error" />
        </Tooltip>
      );
    default:
      return (
        <Tooltip title="Incomplete">
          <ErrorIcon color="yellow" />
        </Tooltip>
      );
  }
}

/**
 * Whether we should collect demographic data for an event. Determines if the event is a type for which we
 * collect demographics. Does not check whether demographics data have been entered for the event.
 */
export const shouldCollectDemographics = (event) => {
  return event.event_type === 'CLASS';
}

/**
 * Whether we should track individual participants for the event. For privacy reasons, we don't track individual
 * participants for youth events/sessions.
 */
export const shouldTrackParticipants = (event) => {
  return event.event_type === 'CLASS'
    && event.curriculum.audience !== 'Youth'
    && event.demographics_calculation !== demographicsCalculation.MANUAL;
}

export const getMaxSessionParticipants = (event) => {
  return shouldTrackParticipants(event) ? event.unique_attendees : event.max_participants;
}

export const getDemographicsSummary = (event, values = null) => {
  let demo = values || {};
  if (!values) {
    demo = parseDemographics(event);
  }
  const participants = getMaxSessionParticipants(event);
  const sums = {
    age: demo.age ? Object.values(demo.age).reduce((acc, val) => acc + (val || 0), 0) : 0,
    gender: demo.gender ? Object.values(demo.gender).reduce((acc, val) => acc + (val || 0), 0) : 0,
    race: demo.race ? Object.values(demo.race).reduce((acc, val) => acc + (val || 0), 0) : 0,
  }
  const groupsValid = {
    age: sums.age === participants,
    gender: sums.gender === participants,
    race: sums.race >= participants,
  }
  return {
    valid: shouldTrackParticipants(event) || Object.values(groupsValid).every(v => v),
    participants,
    sums,
    groupsValid
  };
};

export const maybeParseJson = (value) => {
  if (!value) {
    return {};
  }
  if (typeof value === 'string') {
    return JSON.parse(value);
  }
  return value;
}

export const parseDemographics = (event) => {
  return {
    age: event.demographics?.age ? maybeParseJson(event.demographics.age) : {},
    gender: event.demographics?.gender ? maybeParseJson(event.demographics.gender) : {},
    race: event.demographics?.raceEthnicity ? maybeParseJson(event.demographics.raceEthnicity) : {},
  }
}

// Parse demographics data from the event into values we can use in the form.
export const getDemographicsInputValues = (event, demographicOptions) => {
  if (!event || !demographicOptions) {
    return {};
  }
  const existingValues = parseDemographics(event);
  const ageGroups = getAgeGroups(event, demographicOptions);

  const values = {
    age: {},
    gender: {},
    race: {},
    source: event.demographics?.source,
    comments: event.comments,
  };
  ageGroups.forEach(group => {
    values.age[group.code] = existingValues.age[group.code] || 0;
  });
  demographicOptions.gender.forEach(g => {
    values.gender[g.code] = existingValues.gender[g.code] || 0;
  });
  demographicOptions.raceEthnicity.forEach(r => {
    values.race[r.code] = existingValues.race[r.code] || 0;
  });
  return values;
}

// Gets an array of the relevant age groups for an event, based on curriculum
export const getAgeGroups = (event, demographicOptions) => {
  if (!event || !demographicOptions?.age) {
    return [];
  }

  const existingValues = parseDemographics(event);

  return demographicOptions.age.filter(opt => {
    // Hide options that aren't suitable for the current event's audience
    if (opt.audience === 'ADULTS' && !['Adults', 'Family'].includes(event.curriculum?.audience)) {
      return false;
    }
    if (opt.audience === 'YOUTH' && !['Family', 'Youth'].includes(event.curriculum?.audience)) {
      return false;
    }

    if (opt.active) {
      return true;
    }

    // Include inactive options if the existing data has non-zero values for them
    return existingValues?.age ? !!existingValues?.age[opt.code] : false;
  });
}

/**
 * Checks if demographic data has been entered for an event. This is defined as having a non-zero total in
 * each of the 3 categories (age, gender, race).
 *
 * This does not validate whether the demographic counts match the session participant count. See
 * getDemographicsSummary() for that.
 */
export const hasCompleteDemographics = (event) => {
  if (!event) {
    return false;
  }

  const { sums } = getDemographicsSummary(event);
  return sums.age > 0 && sums.gender > 0 && sums.race > 0;
}

/**
 * Similar to hasCompleteDemographics(), but returns true as long as any category has a sum, instead of all of them.
 */
export const hasDemographicData = (event) => {
  if (!event) {
    return false;
  }

  const { sums } = getDemographicsSummary(event);
  return sums.age > 0 || sums.gender > 0 || sums.race > 0;
}
